import type { ReactNode } from "react";
import { enterClassName, NoticeTile } from "./Notice.tsx";

/**
 * The page-top strip `OfflineBanner`, `ConnectionBanner` and the web client's unverified-address
 * banner draw, whose shape `Notice.tsx` states. A module of its own (2026-09-28) so the strip, which
 * only the authenticated entry draws, is not carried by the chunk the public entry shares with it
 * for the notices.
 *
 * One line is the medium control height whether or not the strip holds an action: the strip's block
 * padding is what the control height leaves around the 28 px action, and the words' first line stands
 * in a box of the action's height, so the tile, the words, the action and the dismiss share one line.
 * A message longer than the strip is wide wraps and the strip grows (2026-10-24, by the owner's
 * decision: it had kept one height and cut a long message at both ends). Once it wraps the tile and
 * the dismiss stay level with the first line, the action drops under the words, aligned with their
 * start, and the strip's words, centred while they fit, read from the start.
 */
const bannerClassName =
  "flex min-h-(--control-height-md) justify-center bg-accent-red-solid px-4 py-[calc((var(--control-height-md)_-_var(--chip-height-md))_/_2)] text-(length:--density-text-size) leading-(--density-text-line) font-(weight:--text-body-strong-weight) text-accent-red-on-solid data-[accent=amber]:bg-accent-amber-solid data-[accent=amber]:text-accent-amber-on-solid";
/** A box of the action's height, which a one-line control or tile centres in. */
const lineBoxClassName = "flex h-(--chip-height-md) flex-none items-center";
/** The words' first line centred in the action's height; a word longer than the strip breaks rather than overflowing it. */
const messageClassName = "min-w-0 py-[calc((var(--chip-height-md)_-_var(--density-text-line))_/_2)] wrap-anywhere";

export interface BannerProps {
  readonly variant: string;
  /** Red, the default, for a connection that is down or degraded; amber where nothing is broken: a
   * request of the person's own, the unverified address (2026-09-28), and a stream the instance
   * refused this account while it keeps answering (2026-10-14). */
  readonly accent?: "red" | "amber";
  readonly role?: "status";
  /** The leading glyph, drawn in `NoticeTile`. */
  readonly tile?: ReactNode;
  /** The small buttons at the chip height, beside the words while they fit and under them once they do not. */
  readonly actions?: ReactNode;
  /** The ghost icon button that hides the strip, at the end of its first line. */
  readonly dismiss?: ReactNode;
  readonly children: ReactNode;
}

export function Banner({ variant, accent = "red", role, tile, actions, dismiss, children }: BannerProps) {
  return (
    <div data-slot="banner" data-variant={variant} data-accent={accent} role={role} className={`${bannerClassName} ${enterClassName}`}>
      <div className="flex min-w-0 items-start gap-2">
        {tile !== undefined && (
          <span className={lineBoxClassName}>
            <NoticeTile>{tile}</NoticeTile>
          </span>
        )}
        <div className="flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1">
          <div data-slot="banner-message" className={messageClassName}>
            {children}
          </div>
          {actions !== undefined && <div className="flex flex-none gap-1">{actions}</div>}
        </div>
        {dismiss !== undefined && <span className={lineBoxClassName}>{dismiss}</span>}
      </div>
    </div>
  );
}
